/**
 * Scan Comparison Page
 *
 * Compares two scans side-by-side showing score deltas,
 * new findings, and resolved findings.
 *
 * URL: /compare?scan1=SCN-XXX&scan2=SCN-YYY
 */

import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, GitCompareArrows } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { PageBackground } from '@/components/ui/PageLayout';
import { Button } from '@/components/ui/Button';
import { useCompareScans } from '@/hooks/useCompareScans';
import ComparisonHeader from '@/components/compare/ComparisonHeader';
import ScoreDelta from '@/components/compare/ScoreDelta';
import FindingsDiff from '@/components/compare/FindingsDiff';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const ScanComparePage = () => {
  useDocumentTitle('Compare Scans');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const scan1 = searchParams.get('scan1');
  const scan2 = searchParams.get('scan2');

  const { data, isLoading, error } = useCompareScans(scan1, scan2);

  if (!scan1 || !scan2) {
    return (
      <PageBackground>
        <AppHeader />
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <AlertCircle className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Two scan IDs required</h2>
          <p className="text-zinc-400 mb-6">
            Navigate to <span className="font-mono text-zinc-300">/compare?scan1=SCN-XXX&scan2=SCN-YYY</span> or
            select two scans from the Reports page.
          </p>
          <Button onClick={() => navigate('/reports')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go to Reports
          </Button>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <AppHeader />
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Back button + title */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <GitCompareArrows className="w-5 h-5 text-zinc-400" />
            <h1 className="text-xl font-semibold text-white">Scan Comparison</h1>
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            <span className="ml-3 text-zinc-400">Comparing scans...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-6 text-center">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-400">{error instanceof Error ? error.message : 'Failed to compare scans'}</p>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            {/* Scan summary cards */}
            <ComparisonHeader
              scan1={{
                scanId: typeof data.scan1 === 'string' ? data.scan1 : data.scan1.scanId,
                label: typeof data.scan1 === 'string' ? 'Baseline scan' : data.scan1.target,
              }}
              scan2={{
                scanId: typeof data.scan2 === 'string' ? data.scan2 : data.scan2.scanId,
                label: typeof data.scan2 === 'string' ? 'Current scan' : data.scan2.target,
              }}
            />

            {/* Score deltas */}
            {data.comparison.scores && (
              <div>
                <h2 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3">Score Changes</h2>
                <ScoreDelta scores={[
                  {
                    label: 'AIVSS',
                    scan1: data.comparison.scores.scan1.aivss,
                    scan2: data.comparison.scores.scan2.aivss,
                    delta: data.comparison.scores.delta.aivss,
                    higherIsWorse: true,
                  },
                  {
                    label: 'CVSS',
                    scan1: data.comparison.scores.scan1.cvss,
                    scan2: data.comparison.scores.scan2.cvss,
                    delta: data.comparison.scores.delta.cvss,
                    higherIsWorse: true,
                  },
                  {
                    label: 'AARS',
                    scan1: data.comparison.scores.scan1.aars,
                    scan2: data.comparison.scores.scan2.aars,
                    delta: data.comparison.scores.delta.aars,
                    higherIsWorse: true,
                    maxScore: 100,
                  },
                  {
                    label: 'Overall',
                    scan1: data.comparison.scores.scan1.overall,
                    scan2: data.comparison.scores.scan2.overall,
                    delta: data.comparison.scores.delta.overall,
                    higherIsWorse: true,
                    maxScore: 100,
                  },
                ]} />
              </div>
            )}

            {/* Findings diff */}
            {data.comparison.findings && (
              <div>
                <h2 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3">Finding Changes</h2>
                <FindingsDiff
                  newFindings={data.comparison.findings.new}
                  resolvedFindings={data.comparison.findings.resolved}
                  unchangedCount={data.comparison.findings.unchangedCount}
                  newBySeverity={data.comparison.findings.newBySeverity}
                  resolvedBySeverity={data.comparison.findings.resolvedBySeverity}
                />
              </div>
            )}

            {/* Minimal comparison (score change only) */}
            {!data.comparison.scores && !data.comparison.findings && data.comparison.scoreChange != null && (
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-6 text-center">
                <p className="text-zinc-400">
                  Score change: <span className="text-white font-semibold">{data.comparison.scoreChange}</span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </PageBackground>
  );
};

export default ScanComparePage;
